import * as React from 'react';
import store from 'store';
import { useCallback, useContext } from 'react';
import {
  BaseTurnstileItem,
  IBaseTurnstileFormValues,
  ITurnstileItemProps,
} from './BaseTurnstile';
import { useSelector } from 'react-redux';
import { IBody, subscribe } from '../../../api/turnstile';
import { IPlayerRootReducerShape } from '../../../store/rootPlayerStore';
import { getIsCustomTurnstileIntegrationEnabled } from '../../../store/customIntegrations/selectors';
import { PlayerExternalAPIContext } from '../PlayerExternalAPIContext';
import {
  getIsTurnstileEmailCollectedStorageKey,
  getTurnstileShowStorageKey,
} from '../../../store/videoItems/utils';

export const PlayerTurnstileItem = (props: ITurnstileItemProps) => {
  const { item, onDismiss } = props;

  const file = useSelector(
    (state: IPlayerRootReducerShape) => state.sourceConfiguration.current?.file
  );
  const funnelId = useSelector(
    (state: IPlayerRootReducerShape) =>
      state.sourceConfiguration?.current?.funnelId
  );
  const playerExternalApi = useContext(PlayerExternalAPIContext);
  const isCustomTurnstileIntegrationEnabled = useSelector(
    getIsCustomTurnstileIntegrationEnabled
  );

  const dismissOnSuccess = useCallback(() => {
    store.set(getTurnstileShowStorageKey(item), new Date().toDateString());
    store.set(
      getIsTurnstileEmailCollectedStorageKey(item),
      new Date().toDateString()
    );
    onDismiss(item);
  }, [item, onDismiss]);

  const dismissOnSkip = useCallback(() => {
    store.set(getTurnstileShowStorageKey(item), new Date().toDateString());
    onDismiss(item);
  }, [item, onDismiss]);

  const handleSkip = useCallback(() => {
    playerExternalApi?.fromEmitter?.timelineTurnstileSkipped.emit();
    dismissOnSkip();
  }, [dismissOnSkip, playerExternalApi]);

  const handleSubmit = useCallback(
    async (values: IBaseTurnstileFormValues) => {
      const valuesToSend: IBody = item.isNameRequired
        ? {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
          }
        : { email: values.email };

      // Custom integration for ClickFunnels
      if (isCustomTurnstileIntegrationEnabled) {
        playerExternalApi?.fromEmitter?.timelineTurnstileCompleted.emit(
          valuesToSend
        );

        dismissOnSuccess();
        return true;
      }

      if (!funnelId && !file) {
        alert(item.failedToSubmitText);
        return false;
      }

      let response: Response | undefined = undefined;
      if (funnelId) {
        response = await subscribe(item.id, valuesToSend, { funnelId });
      } else if (file) {
        response = await subscribe(item.id, valuesToSend, {
          videoId: file.id,
          type: 'voomly',
        });
      }

      if (!response?.ok) {
        if (!response) {
          alert(item.failedToSubmitText);
          return false;
        }

        const result = await response.json();
        if (result?.code === 'TURNSTILE_NOT_CONFIGURED') {
          alert('Turnstile is not configured');
        }
        return false;
      }

      playerExternalApi?.fromEmitter?.timelineTurnstileCompleted.emit(
        valuesToSend
      );

      dismissOnSuccess();
      return true;
    },
    [
      file,
      funnelId,
      item,
      dismissOnSuccess,
      playerExternalApi,
      isCustomTurnstileIntegrationEnabled,
    ]
  );

  return (
    <BaseTurnstileItem
      {...props}
      onSubmit={handleSubmit}
      onSkip={handleSkip}
      isPreviewMode={false}
      titleText={item.titleText}
      subTitleText={item.subTitleText}
      formFirstNameText={item.formFirstNameText || 'First name'}
      formLastNameText={item.formLastNameText || 'Last name'}
      formEmailText={item.formEmailText || 'Email address'}
      formEmailTextRaw={item.formEmailText || 'Email address'}
    />
  );
};
